/**
 * Records kept on this device so they can be opened with no signal — today, inspection reports.
 *
 * ── WHY THIS EXISTS, AND WHAT IT IS ALLOWED TO HOLD ──────────────────────────────────────────
 *
 * Unsent EDITS already survive a dead zone (features/inspections/drafts.ts). That was half of it:
 * the report they belong to came from the server, so an inspector who opened the app in a rural
 * driveway had their notes on the phone and no page to put them on. This is the other half — a
 * read copy of a report, written each time it loads online, and read back only when the server
 * cannot be reached.
 *
 * It is personal information at rest (an address, who lives there, what the last visit found), so
 * the rules are narrow, and docs/SECURITY.md states them:
 *
 *  - ONLY what somebody opened, and only while it is still being worked — booked or in progress.
 *    A signed-off report is dropped the next time it is seen, and nothing is fetched in bulk.
 *  - TAGGED with the user AND the organisation, and only ever read back for that pair. A second
 *    login on the same phone, or the same person switching agency, never sees another's copy.
 *  - GONE on sign-out, and on a session the server has refused — `clearOfflineCopies` deletes the
 *    whole database, not rows. (Unsent drafts are NOT cleared then, deliberately — see SECURITY.md.)
 *  - GONE after a fortnight unopened, so a phone does not carry a report it is never going back to.
 *
 * In `lib`, not in the inspections feature, so signing out can clear it without loading the
 * inspection screen's code — a sign-out from the dashboard has to empty it too.
 *
 * IndexedDB with no library, for `draftStore`'s reasons.
 */

const DB_NAME = 'promanage-offline-copies';
const STORE = 'copies';

/**
 * The shape version. A copy written by an older build may not match what this one renders, and a
 * copy that cannot be trusted is simply not offered — the next online load writes a fresh one.
 */
const SHAPE = 1;

/** A fortnight: a report not opened in that long is not one somebody is about to walk into. */
const KEEP_MS = 14 * 24 * 60 * 60 * 1000;

export interface CopyOwner {
  userId: string;
  orgId: string;
}

export interface CopySummary {
  /** The record's display ref — `IS000012`. */
  ref: string;
  /** Its sequence, which is what the page loads it by. */
  refSeq: number;
  /** A line a person can recognise it by, in a list of what is on this phone. */
  title: string;
  subtitle: string;
  savedAt: string;
}

interface Row extends CopySummary {
  key: string;
  owner: string;
  shape: number;
  value: unknown;
}

const ownerKey = (owner: CopyOwner) => `${owner.orgId}:${owner.userId}`;
const rowKey = (owner: CopyOwner, refSeq: number) => `${ownerKey(owner)}:${String(refSeq)}`;

function isRow(value: unknown): value is Row {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    row.shape === SHAPE &&
    typeof row.key === 'string' &&
    typeof row.owner === 'string' &&
    typeof row.ref === 'string' &&
    typeof row.refSeq === 'number' &&
    typeof row.title === 'string' &&
    typeof row.subtitle === 'string' &&
    typeof row.savedAt === 'string' &&
    'value' in row
  );
}

const fresh = (row: Row, now: number) => now - Date.parse(row.savedAt) < KEEP_MS;

let opened: Promise<IDBDatabase> | null = null;

function database(): Promise<IDBDatabase> {
  opened ??= new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('This browser has no device storage'));
      return;
    }
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => {
      open.result.createObjectStore(STORE, { keyPath: 'key' }).createIndex('owner', 'owner');
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error ?? new Error('Could not open device storage'));
    open.onblocked = () => reject(new Error('Device storage is held open by another tab'));
  });
  // A failed open is not remembered: the next call tries again rather than failing for ever.
  opened.catch(() => {
    opened = null;
  });
  return opened;
}

/** Run `work` in one transaction, resolving when it COMMITS — `draftStore`'s rule. */
async function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore, done: (value: T) => void) => void,
): Promise<T> {
  const db = await database();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    let value: T | undefined;
    let settled = false;
    work(transaction.objectStore(STORE), (result) => {
      value = result;
      settled = true;
    });
    transaction.oncomplete = () => {
      if (settled) resolve(value as T);
      else reject(new Error('Device storage finished without an answer'));
    };
    transaction.onerror = () => reject(transaction.error ?? new Error('Device storage failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Device storage aborted'));
  });
}

/**
 * Keep (or refresh) a copy. Failures are swallowed: a copy is a convenience for later, and a phone
 * that will not store one must not break the page that was just loaded online.
 */
export async function keepCopy(
  owner: CopyOwner,
  summary: Omit<CopySummary, 'savedAt'>,
  value: unknown,
): Promise<void> {
  const row: Row = {
    ...summary,
    key: rowKey(owner, summary.refSeq),
    owner: ownerKey(owner),
    shape: SHAPE,
    savedAt: new Date().toISOString(),
    value,
  };
  try {
    await run<void>('readwrite', (store, done) => {
      store.put(row).onsuccess = () => done(undefined);
    });
  } catch {
    // Nothing kept; the page is unaffected.
  }
}

/** The copy of one record for this owner, or null — including when it is too old to trust. */
export async function readCopy(
  owner: CopyOwner,
  refSeq: number,
): Promise<{ value: unknown; savedAt: string } | null> {
  try {
    const row = await run<unknown>('readonly', (store, done) => {
      const request = store.get(rowKey(owner, refSeq));
      request.onsuccess = () => done(request.result);
    });
    if (!isRow(row) || row.owner !== ownerKey(owner) || !fresh(row, Date.now())) return null;
    return { value: row.value, savedAt: row.savedAt };
  } catch {
    return null;
  }
}

/** Everything kept for this owner, newest first — and the stale ones deleted on the way. */
export async function listCopies(owner: CopyOwner): Promise<CopySummary[]> {
  try {
    const rows = await run<unknown[]>('readwrite', (store, done) => {
      const request = store.index('owner').getAll(ownerKey(owner));
      request.onsuccess = () => {
        const all = request.result as unknown[];
        const now = Date.now();
        for (const row of all) {
          if (!isRow(row) || !fresh(row, now)) {
            const key = (row as { key?: unknown }).key;
            if (typeof key === 'string') store.delete(key);
          }
        }
        done(all);
      };
    });
    const now = Date.now();
    return rows
      .filter(isRow)
      .filter((row) => fresh(row, now))
      .map(({ ref, refSeq, title, subtitle, savedAt }) => ({
        ref,
        refSeq,
        title,
        subtitle,
        savedAt,
      }))
      .sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  } catch {
    return [];
  }
}

/** Drop one copy — the record has been signed off, or is otherwise no longer worth keeping. */
export async function dropCopy(owner: CopyOwner, refSeq: number): Promise<void> {
  try {
    await run<void>('readwrite', (store, done) => {
      store.delete(rowKey(owner, refSeq)).onsuccess = () => done(undefined);
    });
  } catch {
    // Already gone, or no storage — either way there is nothing on the device.
  }
}

/**
 * Delete every copy on this device, for every user. Signing out, or a session the server refused.
 *
 * The whole database rather than one owner's rows, because the moment this is called is the
 * moment nobody on this device is known to be entitled to any of it.
 */
export function clearOfflineCopies(): void {
  if (typeof indexedDB === 'undefined') return;
  const closing = opened;
  opened = null;
  const remove = () => {
    try {
      indexedDB.deleteDatabase(DB_NAME);
    } catch {
      // No storage, so nothing stored.
    }
  };
  // An open connection blocks the delete until it closes, so close ours first.
  if (closing) {
    closing
      .then(
        (db) => db.close(),
        () => undefined,
      )
      .finally(remove);
  } else {
    remove();
  }
}
